import { observable, autorun, makeObservable } from 'mobx'
import * as moment from 'moment-timezone'
import * as R from 'ramda'
import i18n from 'i18next'
import Client from './Client'
import { getDefaultCheckedAdminTagPairs } from '../../utils/helpers'

class ClientStore {
    @observable filteredList = []

    clients = []

    @observable client = new Client({})

    status = {
        active: { name: 'active', label: i18n.t('table.col.status.active'), value: true },
        inactive: { name: 'inactive', label: i18n.t('table.col.status.inactive'), value: false },
    }

    timezones = []

    countries = []

    industries = []

    surveyTypes = []

    currencies = []

    users = []

    flagLabels = {
        isTest: i18n.t('client.form.flags.isTest'),
        isDashboardEnabled: i18n.t('client.form.flags.isDashboardEnabled'),
        isCompanyTableEnabled: i18n.t('client.form.flags.isCompanyTableEnabled'),
        isAdditionalDataImportingEnabled: i18n.t('client.form.flags.isAdditionalDataImportingEnabled'),
        isOnSiteEnabled: i18n.t('client.form.flags.isOnSiteEnabled'),
        isCustomerJourneyEnabled: i18n.t('client.form.flags.isCustomerJourneyEnabled'),
        isInterceptEnabled: i18n.t('client.form.flags.isInterceptEnabled'),
        isEmbedEnabled: i18n.t('client.form.flags.isEmbedEnabled'),
        isQrEnabled: i18n.t('client.form.flags.isQrEnabled'),
        isMultiDivisionEnabled: i18n.t('client.form.flags.isMultiDivisionEnabled'),
        isDeliveryReportEnabled: i18n.t('client.form.flags.isDeliveryReportEnabled'),
        isAdvancedCrossTabEnabled: i18n.t('client.form.flags.isAdvancedCrossTabEnabled'),
        isExhibitMeasurementEnabled: i18n.t('client.form.flags.isExhibitMeasurementEnabled'),
        isRapidSurveysEnabled: i18n.t('client.form.flags.isRapidSurveysEnabled'),
        isCertificationEnabled: i18n.t('client.form.flags.isCertificationEnabled'),
        isAiSummaryEnabled: i18n.t('client.form.flags.isAiSummaryEnabled'),
        isSalesIntelligenceEnabled: i18n.t('client.form.flags.isSalesIntelligenceEnabled'),
    }

    @observable formFields = []

    @observable formLoading = true

    @observable availableTags = []

    @observable recordTags = []

    constructor(store) {
        makeObservable(this)
        this.appStore = store

        autorun(() => {
            if (this.formFields.length > 0) this.formLoading = false
        })
    }

    initSearch = async () => {
        try {
            const data = await this.fetchClients()
            this.clients = data.clients
            this.filteredList = data.clients
        } catch (error) {
            console.error('ClientStore initSearch error ', error)
        }
    }

    initClient = async (clientId, handleCloseModal) => {
        this.formLoading = true
        try {
            [this.timezones, this.countries, this.industries,
                this.surveyTypes, this.currencies, this.users] = await Promise.all([
                this.getTimeZones(),
                this.fetchCountries(),
                this.fetchIndustries(),
                this.fetchSurveyTypes(),
                this.fetchCurrencies(),
                this.fetchUsers(),
            ])

            if (clientId !== 0) {
                const client = await this.fetchClient(clientId)
                this.client = client
            } else {
                this.client = Object.seal(new Client({}))
            }

            this.availableTags = await this.appStore.getAvailableTags({
                oid: 3,
            })

            this.recordTags = await this.appStore.getRecordTags(3, clientId)

            if (clientId === 0) {
                getDefaultCheckedAdminTagPairs(this.availableTags).forEach(([tagId, optionId]) => {
                    this.recordTags[tagId] = [optionId]
                })
            }

            this.defineFormFields(this.timezones, this.countries, this.industries,
                this.surveyTypes, this.currencies, this.users)
        } catch (error) {
            console.error('ClientStore initClient error ', error)
            handleCloseModal()
            // this.appStore.addNotification(null, 'Failed to connect with server', 'error')
        }
    }

    setTag = ({ name, value }) => {
        this.recordTags[name] = value
    }

    saveTags = async (recordId) => {
        const { appStore } = this
        const { availableTags, recordTags } = this
        try {
            appStore.saveTags(recordId, 3, availableTags, recordTags)
        } catch (e) {
            console.error(e)
        }
    }

    fetchClients = () => this.appStore.http.get('/client')

    toggleStatus = (name) => {
        this.status[name].value = !this.status[name].value
        this.search()
    }

    resetFilter = () => {
        this.status = {
            active: { name: 'active', label: 'Active', value: true },
            inactive: { name: 'inactive', label: 'Inactive', value: false },
        }
        this.search()
    }

    search = () => {
        const { clients } = this
        const { searchStr } = this.searchModule

        const activeFiltered = clients
            .filter(({ active }) => (
                active ? this.status.active.value : this.status.inactive.value
            ))

        if (searchStr === '') {
            this.filteredList = activeFiltered
        } else if (!Number.isNaN(parseInt(searchStr, 10))) {
            this.filteredList = activeFiltered
                .filter((d) => d.clientId === parseInt(searchStr, 10))
        } else {
            const re = new RegExp(searchStr, 'i')
            this.filteredList = activeFiltered
                .filter((d) => (
                    d.name.match(re)
                        || d.postcode.match(re)
                        || d.country.match(re)
                ))
        }
    }

    clientEditAccess = (clientId, role) => {
        const superUser = this.appStore.isExploriSuper()
        const allowedRole = role.includes(String(this.appStore.userData.role))
        return (
            (this.appStore.userData.clientId === clientId && allowedRole) || !!superUser
        )
    }

    // ClientEdit
    setField = async ({ value, name }) => {
        if (name.startsWith('status')) {
            const status = name.split('-')[1]
            if (status === 'inactive') this.client.active = !value
            else this.client.active = value
            this.defineFormFields(this.timezones, this.countries, this.industries,
                this.surveyTypes, this.currencies, this.users)
        } else if (name === 'flags') {
            Object.keys(this.client.flags)
                .forEach((flagKey) => { this.client.flags[flagKey] = value.includes(flagKey) })
        } else if (name === 'clearRespondentDetails') {
            this.client[name] = parseInt(value, 10)
        } else this.client[name] = value
    }

    defineFormFields = (timezones, countries, industries, surveyTypes, currencies, users) => {
        const { appStore } = this
        const flagsOrEmpty = R.pathOr({}, ['flags'], this.client)

        this.formFields = [
            {
                type: 'checkbox-reactive',
                cssClass: 'horizontal',
                name: 'clientStatus',
                label: i18n.t('clientStatus'),
                isHide: !appStore.isExploriSuper(),
                fields: [
                    {
                        type: 'checkbox',
                        label: i18n.t('active'),
                        name: 'status-active',
                        checked: this.client.active,
                    },
                    {
                        type: 'checkbox',
                        label: i18n.t('inactive'),
                        name: 'status-inactive',
                        checked: !this.client.active,
                    },
                ],
            },
            {
                type: 'input',
                name: 'name',
                label: i18n.t('name'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.client.name,
                isMandatory: true,
                errorMessage: i18n.t('client.form.error.name'),
            },
            {
                type: 'input',
                name: 'address',
                label: i18n.t('address'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.client.address,
                isMandatory: true,
                errorMessage: i18n.t('client.form.error.address'),
            },
            {
                type: 'input',
                name: 'town',
                label: i18n.t('city'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.client.town,
                isMandatory: true,
                errorMessage: i18n.t('client.form.error.city'),
            },
            {
                type: 'input',
                name: 'postcode',
                label: i18n.t('postcode'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.client.postcode,
                isMandatory: true,
                errorMessage: i18n.t('client.form.error.postcode'),
            },
            {
                type: 'select',
                name: 'country',
                label: i18n.t('country'),
                placeholder: i18n.t('form.select.placeholder', 'Select here'),
                defaultValue: this.client.country,
                options: countries,
                isMandatory: true,
                errorMessage: i18n.t('client.form.error.country'),
            },
            {
                type: 'input',
                name: 'telephone',
                label: i18n.t('tel'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.client.telephone,
                isMandatory: true,
                errorMessage: i18n.t('client.form.error.tel'),
            },
            {
                type: 'input',
                name: 'fax',
                label: i18n.t('fax'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.client.fax,
            },
            {
                type: 'select',
                name: 'timezone',
                label: i18n.t('client_defaultTimezone'),
                placeholder: i18n.t('form.select.placeholder', 'Select here'),
                defaultValue: this.client.timezone,
                options: timezones,
                isMandatory: true,
                errorMessage: i18n.t('client.form.error.timezone'),
            },
            {
                type: 'select',
                name: 'mainContact',
                label: i18n.t('main_contact'),
                placeholder: i18n.t('form.select.placeholder', 'Select here'),
                defaultValue: this.client.mainContact,
                options: users,
                isMandatory: false,
                errorMessage: i18n.t('client.form.error.contact'),
            },
            {
                type: 'checkbox-group',
                name: 'flags',
                label: i18n.t('featureFlags'),
                options: Object.keys(flagsOrEmpty).map(
                    (flagKey) => (
                        { label: R.pathOr(flagKey, [flagKey])(this.flagLabels), value: flagKey }
                    ),
                ),
                value: Object.keys(flagsOrEmpty).filter(
                    (flagKey) => flagsOrEmpty[flagKey] === true,
                ),
                isHide: !appStore.isExploriSuper(),
            },
            {
                type: 'input',
                inputType: 'number',
                name: 'clearRespondentDetails',
                label: i18n.t('client_clearResponentDetails'),
                placeholder: i18n.t('form.input.placeholder', 'Enter here'),
                defaultValue: this.client.clearRespondentDetails,
                isMandatory: true,
                errorMessage: i18n.t('client.form.error.clearMonths'),
                min: '0',
                isHide: !appStore.isExploriSuper(),
            },
            {
                type: 'checkbox-group',
                name: 'locales',
                label: i18n.t('languages'),
                options: this.getLocales(),
                value: R.pathOr([], ['locales'], this.client),
                isHide: !appStore.isExploriSuper(),
            },
            {
                type: 'checkbox-group',
                name: 'industry',
                label: i18n.t('industries'),
                options: industries,
                value: R.pathOr([], ['industry'], this.client),
                isHide: !appStore.isExploriSuper(),
            },
            {
                type: 'checkbox-group',
                name: 'surveyTypes',
                label: i18n.t('survey_types'),
                options: surveyTypes,
                value: R.pathOr([], ['surveyTypes'], this.client),
                isHide: !appStore.isExploriSuper(),
            },
            {
                type: 'checkbox-group',
                name: 'currencies',
                label: i18n.t('currencies'),
                options: currencies,
                value: R.pathOr([], ['currencies'], this.client),
                isHide: !appStore.isExploriSuper(),
            },
        ]
    }

    resetFormState = () => {
        this.formFields = []
    }

    fetchClient = (clientId) => this.appStore.http.get(`/client/${clientId}`)

    getTimeZones = async () => (
        R.sortBy(R.prop('offset'))(
            moment.tz.names()
                .filter((tz) => !tz.match(/^Etc\//gi))
                .map((tz) => {
                    const offset = moment.tz(tz).format('Z')
                    return {
                        value: tz,
                        label: `${tz}, (GMT ${offset})`,
                        offset: Number(offset.replace(':', '.')),
                    }
                }),
        )
    )

    fetchCountries = async () => {
        const countryData = await this.appStore.http.get('/countrylangs?langcodes=en_GB')
        const countries = countryData.map((country) => (
            { value: country.countrycode, label: country.langvalue }
        ))
        return countries
    }

    fetchIndustries = async () => {
        const superUser = this.appStore.isSuperUser() && this.appStore.isExploriClient()
        if (superUser) {
            const industryData = await this.appStore.http.get('/industry/all')
            const industries = industryData.map((industry) => (
                { value: industry.code, label: industry.name }
            ))
            return industries
        }
        return []
    }

    fetchSurveyTypes = async () => {
        const superUser = this.appStore.isSuperUser() && this.appStore.isExploriClient()
        if (superUser) {
            const surveyTypeData = await this.appStore.http.get('/surveytype/all')
            const surveyTypes = surveyTypeData.map((surveyType) => (
                { value: surveyType.code, label: surveyType.name }
            ))
            return surveyTypes
        }
        return []
    }

    fetchCurrencies = async () => {
        const superUser = this.appStore.isSuperUser() && this.appStore.isExploriClient()
        const currencies = (superUser)
            ? await this.appStore.http.get('/refcode/currency')
            : []

        return currencies.map((currency) => (
            { value: currency, label: currency }
        ))
    }

    fetchUsers = async () => {
        const usersData = await this.appStore.http.get('/users')
        const users = usersData.users.filter((u) => (
            u.active && u.email.toLowerCase().indexOf('@explori') === -1))
            .sort((a, b) => a.firstname.localeCompare(b.firstname))
            .map((user) => (
                { value: user.userId, label: `${user.firstname} ${user.lastname} <${user.email}>` }
            ))
        return users
    }

    fetchCountries = async () => {
        const countryData = await this.appStore.http.get('/countrylangs?langcodes=en_GB')
        const countries = countryData.map((country) => (
            { value: country.countrycode, label: country.langvalue }
        ))
        return countries
    }

    getLocales = () => this.appStore.localeOptions

    saveClient = async (clientId, handleCloseModal) => {
        try {
            if (clientId === 0) {
                const response = await this.appStore.http.post('/client', JSON.stringify(this.client))
                await this.saveTags(response)
                this.appStore.addNotification({ body: 'Client Added', level: 'success' })
                handleCloseModal()
            } else {
                await this.appStore.http.put(`/client/${clientId}`, JSON.stringify(this.client))
                this.appStore.addNotification({ body: 'Client Updated', level: 'success' })
                await this.saveTags(clientId)
                handleCloseModal()
            }
        } catch (e) {
            console.error('ClientStore save error ', e)
            // handleCloseModal()
            if (clientId === '0') {
                this.appStore.handleError({ body: 'Unable to add client', level: 'error' })
            } else {
                this.appStore.handleError({ body: 'Unable to update client', level: 'error' })
            }
        }
    }
}

export default ClientStore
